// /src/pages/HomePage.js
//
// The landing page — names only, with one anchored figure.
//
// At rest the page is type: twelve entries in two columns, each a year and the
// project name with its descriptor trailing it. Everything a visitor needs to
// choose where to go is readable without moving the cursor.
//
// WHERE THE IMAGE GOES. In a fixed slot in the right margin, sticky to the
// viewport, never overlapping anything. Because the slot never moves and never
// covers the names, the image reads as the page answering rather than as an
// object arriving over it. The slot hangs from the section's hairline, level
// with the filter pills, so most of its height sits beside the pills and the
// names instead of holding the page open beneath them.
//
// The margin is what makes this work at rest. The masthead is capped at 60ch
// and the names occupy two columns, so the empty right third reads as a
// deliberate margin in a left-weighted composition rather than as a hole
// waiting to be filled. Set PREVIEW_AT_REST to "first" if you would rather the
// slot always held something — then hovering only ever swaps one image for
// another and nothing appears from nothing.
//
// Below lg there is no hover to answer, so the slot is not rendered and each
// entry carries a small thumbnail instead.
//
// Layout follows the primary shell: 72px header offset, max-w-[1440px],
// px-6 lg:px-12 gutters, square corners, hairlines, Roboto + mono labels. The
// figure is the aspect-square, object-contain, hairline-outlined shape the
// design system already documents for the rail.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import LandingFilterBar from "../designSystem/components/home/LandingFilterBar";
import LandingMasthead from "../designSystem/components/home/LandingMasthead";
import projectsData from "../designSystem/data/projectsData";
import {
  FILTER_CATEGORIES,
  countsByFilter,
  filterProjects,
} from "../designSystem/utils/projectFilters";
import {
  homeEmptyStateClass,
  homeEntryThumbClass,
  homePreviewCaptionClass,
  homePreviewFrameClass,
  homeTitleDescriptorClass,
  homeTitleEntryClass,
  homeTitleNameClass,
  homeTitleYearClass,
  primaryPageShellClass,
} from "../designSystem/tokens";

// --- Dials ----------------------------------------------------------------
// PREVIEW_AT_REST — "none" keeps the page type-only until something is pointed
//                   at; "first" parks the newest project in the slot so it is
//                   never empty and hovering only ever swaps one image for
//                   another.
// DIM_INACTIVE    — drop the other names to muted while one is active.
const PREVIEW_AT_REST = "none";
const DIM_INACTIVE = true;

// An anchored figure should settle rather than snap, or the fade reads as a
// flicker in the corner of the eye.
const PREVIEW_FADE = { duration: 0.24, ease: "easeOut" };

export default function HomePage() {
  const [activeFilter, setActiveFilter] = useState("All");
  const [activeIndex, setActiveIndex] = useState(null);

  const counts = useMemo(() => countsByFilter(projectsData), []);
  const projects = useMemo(
    () => filterProjects(projectsData, activeFilter),
    [activeFilter]
  );

  useEffect(() => setActiveIndex(null), [activeFilter]);

  // Nothing in the resting page requests these, so warm them up front or the
  // first pass across the grid waits on the network.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(hover: hover)").matches) return;
    projects.forEach(({ mediaSrc }) => {
      if (!mediaSrc) return;
      const image = new Image();
      image.src = mediaSrc;
    });
  }, [projects]);

  const clearActive = useCallback(() => setActiveIndex(null), []);

  const activeProject =
    activeIndex != null
      ? projects[activeIndex] || null
      : PREVIEW_AT_REST === "first"
        ? projects[0] || null
        : null;

  return (
    <main
      className="
        min-h-screen w-full
        bg-white dark:bg-black
        pt-[72px] box-border
        transition-colors duration-500
      "
    >
      {/* No bottom padding here — each column below carries its own, so the
          figure's footprint and the space under the names overlap instead of
          stacking. */}
      <div className={primaryPageShellClass}>
        <LandingMasthead />

        {/* Three equal columns under one hairline: pills and names take the
            first two, the figure takes the third. Outer and inner gaps match,
            so all three measure the same — the page is one grid rather than a
            body plus a sidebar.

            The hairline is the section's, not the pills', so it runs the full
            width while the figure hangs from it beside the pills. Starting
            the figure there, rather than down at the first name, spends its
            height next to the filter instead of under the names. */}
        <section
          className="
            mt-12 lg:mt-16
            border-t border-border dark:border-neutral-800
            pt-4 lg:pt-5
            grid grid-cols-1 lg:grid-cols-3 gap-x-10 xl:gap-x-14
          "
        >
          <div className="lg:col-span-2 min-w-0">
            <LandingFilterBar
              filters={FILTER_CATEGORIES}
              counts={counts}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
            />

            {projects.length === 0 ? (
              <p className={homeEmptyStateClass}>Nothing under this filter yet.</p>
            ) : (
              // Rows pack to the top (content-start) at their own height. If
              // the list is ever stretched to match the figure beside it, the
              // spare height must not be shared out between its rows — or the
              // fewer names a filter leaves, the further apart they drift.
              //
              // The space under the names is the list's own margin, mirroring
              // the section's top margin. The section ends at whichever
              // reaches lower: the names plus that margin, or the figure plus
              // its smaller one. So the figure borrows the room under a short
              // list rather than adding a page-bottom padding beneath itself.
              <ul
                className="
                  list-none p-0 m-0 mt-8 lg:mt-10 mb-12 lg:mb-16
                  content-start
                  grid grid-cols-1 sm:grid-cols-2
                  gap-x-10 xl:gap-x-14 gap-y-6 lg:gap-y-7
                "
                onMouseLeave={clearActive}
              >
                {projects.map((project, i) => (
                  <li key={project.title}>
                    <TitleEntry
                      project={project}
                      isDimmed={
                        DIM_INACTIVE && activeIndex != null && activeIndex !== i
                      }
                      onActivate={() => setActiveIndex(i)}
                      onDeactivate={clearActive}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {projects.length > 0 && <PreviewSlot project={activeProject} />}
        </section>
      </div>
    </main>
  );
}

/**
 * One entry, shaped like a row of the About timeline: year in a narrow mono
 * column, then the name in ink with its descriptor trailing in grey. The
 * category is not shown — the filter above already speaks for it.
 *
 * A thumbnail rides along below lg, where there is no hover to answer.
 */
const TitleEntry = ({ project, isDimmed, onActivate, onDeactivate }) => {
  const { title, subtitle, year, mediaSrc, invert, targetUrl } = project;

  return (
    <Link
      to={targetUrl}
      data-cursor="pointer"
      className={`${homeTitleEntryClass} clickable cursor-none`}
      onMouseEnter={onActivate}
      onFocus={onActivate}
      onBlur={onDeactivate}
    >
      <span className={homeEntryThumbClass} aria-hidden="true">
        {mediaSrc && (
          <img
            src={mediaSrc}
            alt=""
            loading="lazy"
            decoding="async"
            className={`absolute inset-0 h-full w-full object-contain object-center ${
              invert ? "dark:invert" : ""
            }`}
          />
        )}
      </span>

      <span className="min-w-0 flex-1 flex flex-col sm:flex-row sm:gap-4">
        <span className={homeTitleYearClass}>{year}</span>
        <span className={`${homeTitleNameClass(isDimmed)} min-w-0 block`}>
          {title}
          {subtitle && (
            <span className={homeTitleDescriptorClass}> &middot; {subtitle}</span>
          )}
        </span>
      </span>
    </Link>
  );
};

/**
 * The anchored figure. Sticky, so it holds its place while the names scroll
 * past. The invisible block reserves the height; the figure is absolutely
 * positioned over it so the outgoing and incoming images can cross-fade
 * without either one moving the layout.
 *
 * The caption carries role and tools — the two fields the names do not show —
 * so the slot earns its width instead of only decorating it.
 */
const PreviewSlot = ({ project }) => (
  // No width of its own — it is the third column, so the grid sizes it to
  // match the two name columns exactly. The top padding is the pills' own
  // py-2, so the frame's top edge sits on their line of text rather than on
  // the padding above it. The bottom padding is only the gap kept between the
  // caption and the footer.
  <div className="hidden lg:block min-w-0 pt-2 pb-8">
    <div className="sticky top-[104px]">
      <div className="relative w-full">
        <div aria-hidden="true" className="invisible">
          <div className="aspect-square" />
          <p className={homePreviewCaptionClass}>&nbsp;</p>
        </div>

        <AnimatePresence initial={false}>
          {project && (
            <motion.figure
              key={project.title}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={PREVIEW_FADE}
              className="absolute inset-0 m-0"
            >
              <div className="relative w-full aspect-square">
                <div className={homePreviewFrameClass}>
                  {project.mediaSrc && (
                    <img
                      src={project.mediaSrc}
                      alt={`${project.title} — preview`}
                      decoding="async"
                      className={`absolute inset-0 h-full w-full object-contain object-center ${
                        project.invert ? "dark:invert" : ""
                      }`}
                    />
                  )}
                </div>
              </div>

              <figcaption className={homePreviewCaptionClass}>
                {[project.role, formatTools(project.tools)]
                  .filter(Boolean)
                  .join(" \u00b7 ")}
              </figcaption>
            </motion.figure>
          )}
        </AnimatePresence>
      </div>
    </div>
  </div>
);

const formatTools = (tools) =>
  Array.isArray(tools) ? tools.join(", ") : tools || "";
